/**
 * roomTheme – what the room around the desk looks like for a lesson, and
 * where each piece stands.
 *
 * Every lesson used to get the same oak desk in an empty room. The room now
 * follows the lesson: a Geography class gets a stone desk with rock samples
 * and a globe, History gets clay pots, scrolls and a broken column, English a
 * library, Chemistry a lab with glassware, and so on — with a brighter,
 * playful variant for classes 1–5.
 *
 * Which pieces appear, and where, is chosen by a random generator seeded with
 * the lesson (chapter + topic), so every lesson looks different, but every
 * viewer in a live class — and every revisit — builds the same room. "Shuffle"
 * in Studio bumps `propSeed` for a new arrangement.
 *
 * Pure: a theme and a frame in, positions out. The meshes are built by
 * propMeshes.ts; the Studio preview draws from this same function.
 *
 * Placement rules, so the room adds atmosphere without getting in the way:
 *   - never on the desk, and never between the viewer and the desk;
 *   - nothing behind the desk, so the models always have a clean background;
 *   - nothing in the lesson panel's view cone;
 *   - props never overlap each other, and stay at a comfortable distance.
 */

import * as THREE from 'three';

import type { LayoutPreset } from './lessonLayout';
import type { DeskFinish } from './deskMesh';
import { bearing, bearingTowards, yawTowards, type WorkspaceFrame } from './workspace';

export const THEME_IDS = [
  'geography',
  'history',
  'language',
  'biology',
  'chemistry',
  'physics',
  'maths',
  'science_primary',
  'science',
  'computing',
  'social',
  'general',
] as const;
export type ThemeId = (typeof THEME_IDS)[number];
export type GradeBand = 'young' | 'senior';

export const THEME_LABELS: Record<ThemeId, string> = {
  geography: 'Geography — rocks, minerals, globe',
  history: 'History — pottery, scrolls, ruins',
  language: 'Language — library, books, writing',
  biology: 'Biology — plants, microscope, specimens',
  chemistry: 'Chemistry — glassware, molecules',
  physics: 'Physics — pendulum, prisms, magnets',
  maths: 'Maths — solids, abacus, drawing tools',
  science_primary: 'Science (primary) — plants, nature',
  science: 'Science — lab bench',
  computing: 'Computing — computer, circuits, robot',
  social: 'Social science — globe, flag, books',
  general: 'General — bookshelf, plant, globe',
};

/** Pieces that stand on the floor. */
export type FloorKind =
  | 'rocks'
  | 'globe'
  | 'strata'
  | 'amphora'
  | 'column'
  | 'bookshelf'
  | 'plant'
  | 'easel'
  | 'flag'
  | 'pendulum'
  | 'robot'
  | 'bigBlocks'
  | 'volcano'
  | 'weatherStation'
  | 'tree'
  | 'telescope'
  | 'treasureChest'
  | 'shieldSpears'
  | 'lectern'
  | 'aquarium'
  | 'labCabinet'
  | 'solidsPlinth';

/** Small pieces, shown on a side table. */
export type SmallKind =
  | 'crystals'
  | 'compass'
  | 'scrolls'
  | 'oilLamp'
  | 'coins'
  | 'sundial'
  | 'bookStack'
  | 'inkwell'
  | 'readingLamp'
  | 'microscope'
  | 'specimenJars'
  | 'dnaModel'
  | 'flasks'
  | 'tubeRack'
  | 'molecule'
  | 'prism'
  | 'newtonsCradle'
  | 'magnets'
  | 'solids'
  | 'abacus'
  | 'setSquares'
  | 'magnifier'
  | 'wateringCan'
  | 'monitor'
  | 'circuitBoard'
  | 'ballotBox'
  | 'letterBlocks'
  | 'fossil'
  | 'hourglass'
  | 'openBook'
  | 'typewriter'
  | 'seedlings'
  | 'batteryBulb'
  | 'tangram'
  | 'dice';

/** What an easel board shows. */
export type EaselBoard =
  | 'map'
  | 'letters'
  | 'periodic'
  | 'graph'
  | 'chalk'
  | 'timeline'
  | 'poem'
  | 'cell'
  | 'circuit'
  | 'geometry'
  | 'code';

/** Footprint radius and height of each floor piece, metres (before ±10% scale). */
export const FLOOR_SIZE: Record<FloorKind, { radius: number; height: number }> = {
  rocks: { radius: 0.35, height: 0.35 },
  globe: { radius: 0.28, height: 1.1 },
  strata: { radius: 0.22, height: 0.9 },
  amphora: { radius: 0.3, height: 0.75 },
  column: { radius: 0.42, height: 1.25 },
  bookshelf: { radius: 0.5, height: 1.7 },
  plant: { radius: 0.3, height: 1.1 },
  easel: { radius: 0.5, height: 1.6 },
  flag: { radius: 0.42, height: 1.95 },
  pendulum: { radius: 0.3, height: 1.2 },
  robot: { radius: 0.25, height: 0.7 },
  bigBlocks: { radius: 0.35, height: 0.45 },
  volcano: { radius: 0.45, height: 0.6 },
  weatherStation: { radius: 0.35, height: 1.6 },
  tree: { radius: 0.45, height: 1.7 },
  telescope: { radius: 0.45, height: 1.4 },
  treasureChest: { radius: 0.38, height: 0.5 },
  shieldSpears: { radius: 0.42, height: 1.75 },
  lectern: { radius: 0.32, height: 1.25 },
  aquarium: { radius: 0.45, height: 1.1 },
  labCabinet: { radius: 0.5, height: 1.7 },
  solidsPlinth: { radius: 0.4, height: 1.05 },
};

/** A side table's footprint radius and its top height. */
export const SIDE_TABLE = { width: 0.7, depth: 0.45, height: 0.75, radius: 0.42 };

/**
 * A theme's catalogue. Every topic of a chapter takes its own slice of it
 * (planProps), so the pools are sized for variety: at least 2 signature
 * pieces, 4 floor pieces that are not signature pieces, and 8 tabletop items: enough for
 * neighbouring topics never to share a signature piece, a floor piece or a
 * table item.
 */
interface ThemeSpec {
  finish: DeskFinish;
  /** One of these stands in every room: what makes the theme recognisable. */
  signature: FloorKind[];
  floor: FloorKind[];
  small: SmallKind[];
  boards: EaselBoard[];
}

const THEMES: Record<ThemeId, ThemeSpec> = {
  geography: {
    finish: 'stone',
    signature: ['rocks', 'globe', 'volcano', 'strata', 'weatherStation'],
    floor: ['plant', 'easel', 'tree', 'telescope', 'rocks'],
    small: ['crystals', 'compass', 'fossil', 'magnifier', 'hourglass', 'openBook', 'sundial', 'bookStack'],
    boards: ['map', 'graph'],
  },
  history: {
    finish: 'aged',
    signature: ['amphora', 'column', 'treasureChest', 'shieldSpears'],
    floor: ['easel', 'lectern', 'plant', 'amphora', 'bookshelf'],
    small: ['scrolls', 'oilLamp', 'coins', 'sundial', 'hourglass', 'fossil', 'openBook', 'inkwell'],
    boards: ['map', 'timeline'],
  },
  language: {
    finish: 'walnut',
    signature: ['bookshelf', 'lectern'],
    floor: ['easel', 'plant', 'globe', 'tree'],
    small: ['bookStack', 'inkwell', 'readingLamp', 'typewriter', 'openBook', 'hourglass', 'magnifier', 'scrolls'],
    boards: ['letters', 'poem'],
  },
  biology: {
    finish: 'lab',
    signature: ['aquarium', 'tree', 'labCabinet', 'plant'],
    floor: ['easel', 'bookshelf', 'rocks', 'globe'],
    small: ['microscope', 'specimenJars', 'dnaModel', 'magnifier', 'seedlings', 'fossil', 'wateringCan', 'hourglass'],
    boards: ['cell', 'chalk'],
  },
  chemistry: {
    finish: 'lab',
    signature: ['labCabinet', 'easel'],
    floor: ['bookshelf', 'plant', 'globe', 'rocks'],
    small: ['flasks', 'tubeRack', 'molecule', 'batteryBulb', 'magnifier', 'crystals', 'hourglass', 'microscope'],
    boards: ['periodic', 'graph'],
  },
  physics: {
    finish: 'lab',
    signature: ['pendulum', 'telescope'],
    floor: ['easel', 'bookshelf', 'globe', 'robot'],
    small: ['prism', 'newtonsCradle', 'magnets', 'compass', 'batteryBulb', 'hourglass', 'circuitBoard', 'magnifier'],
    boards: ['graph', 'circuit'],
  },
  maths: {
    finish: 'maple',
    signature: ['solidsPlinth', 'easel'],
    floor: ['bookshelf', 'plant', 'globe', 'robot'],
    small: ['solids', 'abacus', 'setSquares', 'tangram', 'dice', 'hourglass', 'bookStack', 'openBook'],
    boards: ['geometry', 'graph'],
  },
  science_primary: {
    finish: 'pastel',
    signature: ['plant', 'globe', 'tree', 'aquarium'],
    floor: ['rocks', 'easel', 'telescope', 'bookshelf'],
    small: ['magnifier', 'wateringCan', 'crystals', 'seedlings', 'fossil', 'compass', 'hourglass', 'openBook'],
    boards: ['chalk', 'cell'],
  },
  science: {
    finish: 'lab',
    signature: ['labCabinet', 'telescope', 'aquarium', 'plant'],
    floor: ['easel', 'bookshelf', 'globe', 'pendulum'],
    small: ['microscope', 'flasks', 'magnifier', 'molecule', 'batteryBulb', 'prism', 'specimenJars', 'hourglass'],
    boards: ['periodic', 'chalk', 'circuit'],
  },
  computing: {
    finish: 'laminate',
    signature: ['robot', 'bookshelf'],
    floor: ['plant', 'easel', 'globe', 'solidsPlinth'],
    small: ['monitor', 'circuitBoard', 'batteryBulb', 'dice', 'tangram', 'openBook', 'hourglass', 'bookStack'],
    boards: ['code', 'circuit'],
  },
  social: {
    finish: 'wood',
    signature: ['globe', 'flag', 'lectern'],
    floor: ['bookshelf', 'easel', 'plant', 'column'],
    small: ['bookStack', 'ballotBox', 'compass', 'openBook', 'coins', 'scrolls', 'hourglass', 'inkwell'],
    boards: ['map', 'timeline'],
  },
  general: {
    finish: 'wood',
    signature: ['bookshelf', 'globe', 'plant'],
    floor: ['easel', 'tree', 'lectern', 'telescope'],
    small: ['bookStack', 'magnifier', 'hourglass', 'openBook', 'compass', 'crystals', 'inkwell', 'readingLamp'],
    boards: ['chalk', 'map'],
  },
};

/** A theme's catalogue, for tests and Studio. */
export function themeCatalogue(id: ThemeId): Readonly<ThemeSpec> {
  return THEMES[id];
}

/** Subject names as the curriculum spells them (see getSubjectNameByLanguage). */
function themeForSubject(subject: string, band: GradeBand): ThemeId {
  const s = subject.trim().toLowerCase();
  if (!s) return 'general';
  if (s.includes('geograph')) return 'geography';
  if (s.includes('histor')) return 'history';
  if (s.includes('english') || s.includes('hindi') || s.includes('language') || s.includes('literature')) return 'language';
  if (s.includes('biolog')) return 'biology';
  if (s.includes('chemis')) return 'chemistry';
  if (s.includes('physic')) return 'physics';
  if (s.startsWith('math') || s.includes('mathemat')) return 'maths';
  if (s.includes('computer') || s.includes('coding') || s.includes('informatics')) return 'computing';
  if (s.includes('social') || s.includes('civic') || s.includes('political')) return 'social';
  if (s === 'evs' || s.includes('environment') || s.includes('science')) {
    return band === 'young' ? 'science_primary' : 'science';
  }
  return 'general';
}

const LAB_THEMES: ThemeId[] = ['biology', 'chemistry', 'physics', 'science'];

export interface ThemeInput {
  subject?: string | null;
  /** 1–12; anything unparseable counts as senior. */
  classNumber?: number | null;
  preset?: LayoutPreset;
  /** From the lesson's layout: 'auto' or a theme id. */
  override?: string | null;
}

export interface ResolvedTheme {
  id: ThemeId;
  band: GradeBand;
  finish: DeskFinish;
}

export function themeFor(input: ThemeInput): ResolvedTheme {
  const n = input.classNumber;
  const band: GradeBand = typeof n === 'number' && Number.isFinite(n) && n >= 1 && n <= 5 ? 'young' : 'senior';
  let id: ThemeId;
  if (input.override && (THEME_IDS as readonly string[]).includes(input.override)) {
    id = input.override as ThemeId;
  } else {
    id = themeForSubject(input.subject ?? '', band);
    // A Lab bench lesson is a lab, whatever the subject says.
    if (input.preset === 'lab_bench' && !LAB_THEMES.includes(id)) {
      id = id === 'science_primary' ? 'science' : 'chemistry';
    }
  }
  const finish: DeskFinish = input.preset === 'lab_bench' ? 'lab' : band === 'young' && THEMES[id].finish !== 'lab' ? 'pastel' : THEMES[id].finish;
  return { id, band, finish };
}

/** Parse "Class 3", "3", 3 → 3. */
export function classNumberOf(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const m = typeof value === 'string' ? value.match(/\d+/) : null;
  return m ? Number(m[0]) : null;
}

/** FNV-1a, so the same lesson always seeds the same room. */
export function hashSeed(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function roomSeed(chapterId: string | null | undefined, topicId: string | null | undefined, propSeed = 0): number {
  return hashSeed(`${chapterId ?? ''}:${topicId ?? ''}:${propSeed}`);
}

/** Deterministic random in [0, 1). */
export function seededRandom(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 2 ** 32;
  };
}

export interface PlacedFloorProp {
  type: 'floor';
  kind: FloorKind;
  /** Floor position, world space (y = ground). */
  x: number;
  z: number;
  yaw: number;
  scale: number;
  radius: number;
  height: number;
  variant: number;
  easel?: EaselBoard;
}

export interface PlacedSideTable {
  type: 'table';
  x: number;
  z: number;
  yaw: number;
  radius: number;
  height: number;
  items: { kind: SmallKind; variant: number }[];
}

export type PlacedProp = PlacedFloorProp | PlacedSideTable;

export interface RoomPlan {
  theme: ResolvedTheme;
  props: PlacedProp[];
}

/** Room around the viewer where props may stand, metres from the anchor. */
export const PROP_RING = { min: 1.3, max: 2.8 };
/** Furthest to either side a prop may stand, degrees. */
export const PROP_MAX_BEARING = 100;
/** Clear space around the desk, and between props. */
export const DESK_MARGIN = 0.3;
export const PROP_GAP = 0.15;
/** Clear band either side of the desk's angular extent: nothing stands behind the models. */
export const BEHIND_DESK_MARGIN_DEG = 8;
/** Clear margin around the lesson panel's view cone. */
export const PANEL_MARGIN_DEG = 5;

interface PanelCone {
  bearingDeg: number;
  halfWidthDeg: number;
}

export interface Obstacles {
  deskHalfAngleDeg: number;
  panel: PanelCone | null;
}

/** Everything a prop must keep clear of, from the frame. */
export function obstaclesOf(frame: WorkspaceFrame, panel: PanelCone | null): Obstacles {
  const a = frame.anchor;
  const toDesk = Math.hypot(frame.dock.center.x - a.position.x, frame.dock.center.z - a.position.z);
  // The models' background: the desk's angular half-width, measured at its
  // centre. Props stand beyond the desk (the ring starts past it), so this is
  // the sector they would appear behind the models in. The desk itself is kept
  // clear separately, by its rectangle.
  const deskHalfAngleDeg = THREE.MathUtils.radToDeg(Math.atan2(frame.dock.width / 2, Math.max(0.05, toDesk)));
  return { deskHalfAngleDeg, panel };
}

/**
 * Whether a prop of radius `r` at world (x, z) is clear of the desk, the view
 * to the models, and the panel. Exported for the tests.
 */
export function propIsClear(frame: WorkspaceFrame, obstacles: Obstacles, x: number, z: number, r: number): boolean {
  const a = frame.anchor;
  const dx = x - a.position.x;
  const dz = z - a.position.z;
  const dist = Math.hypot(dx, dz);
  if (dist < PROP_RING.min || dist > PROP_RING.max) return false;
  const b = bearingTowards(a.forward, a.position, new THREE.Vector3(x, 0, z));
  // The angle the prop's footprint takes up either side of its centre.
  const angR = THREE.MathUtils.radToDeg(Math.asin(Math.min(1, r / Math.max(dist, r))));
  if (Math.abs(b) > PROP_MAX_BEARING) return false;
  // Desk, the band in front of it and everything behind it: one angular sector.
  if (Math.abs(b) - angR < obstacles.deskHalfAngleDeg + BEHIND_DESK_MARGIN_DEG) return false;
  // Desk rectangle itself (belt and braces for a prop just outside the sector).
  const c = frame.dock.center;
  const ux = Math.cos(frame.dock.yaw);
  const uz = -Math.sin(frame.dock.yaw);
  const vx = Math.sin(frame.dock.yaw);
  const vz = Math.cos(frame.dock.yaw);
  const lu = (x - c.x) * ux + (z - c.z) * uz;
  const lv = (x - c.x) * vx + (z - c.z) * vz;
  if (Math.abs(lu) < frame.dock.width / 2 + DESK_MARGIN + r && Math.abs(lv) < frame.dock.depth / 2 + DESK_MARGIN + r) return false;
  // The lesson panel's view.
  if (obstacles.panel) {
    const off = Math.abs(b - obstacles.panel.bearingDeg);
    if (off - angR < obstacles.panel.halfWidthDeg + PANEL_MARGIN_DEG) return false;
  }
  return true;
}

export interface PlanPropsOptions {
  /** The panel's bearing and angular width, to keep its view clear. */
  panel?: { bearingDeg: number; widthDeg: number } | null;
  /**
   * Which topic of the chapter this room is for (0-based), and the chapter's
   * seed. Each topic takes its own slice of the theme's catalogue, so the
   * topics of one chapter do not all get the same pieces. Without it, the room
   * is chosen as for a chapter's first topic.
   */
  topicIndex?: number;
  chapterSeed?: number;
}

/** A copy of `list` in an order fixed by `rand`. */
function shuffled<T>(list: readonly T[], rand: () => number): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** `count` items of `list` from position `start`, wrapping around. */
function windowOf<T>(list: readonly T[], start: number, count: number): T[] {
  if (list.length === 0) return [];
  return Array.from({ length: Math.min(count, list.length) }, (_, i) => list[(start + i) % list.length]);
}

/**
 * Which pieces a topic's room gets, before any are placed.
 *
 * The chapter fixes one shuffled order of the theme's catalogue; topic k takes
 * the k-th signature piece and the k-th window of floor pieces, table items and
 * easel boards. Consecutive topics therefore get different pieces wherever the
 * catalogue has enough of them (it is sized to), and a chapter reads as one
 * subject with a fresh room per topic.
 */
export function topicSelection(theme: ResolvedTheme, chapterSeed: number, topicIndex: number) {
  const spec = THEMES[theme.id];
  const rand = seededRandom(chapterSeed ^ 0x9e3779b9);
  const k = Math.max(0, Math.floor(topicIndex));
  const signatures = shuffled(spec.signature, rand);
  const floors = shuffled(spec.floor, rand);
  const smalls = shuffled([...new Set<SmallKind>(theme.band === 'young' ? [...spec.small, 'letterBlocks'] : spec.small)], rand);
  const boards = shuffled(spec.boards, rand);
  const signature = signatures[k % signatures.length];
  // Extras come from the non-signature pieces, a list fixed for the chapter,
  // so topic k's window and topic k+1's never overlap (the pools hold 4+).
  const extras = windowOf(
    floors.filter((f) => !spec.signature.includes(f)),
    k * 2,
    2
  );
  return {
    signature,
    extras,
    // Each topic's own block of 4 table items, shared by its tables.
    tableItems: windowOf(smalls, k * 4, 4),
    board: boards[k % boards.length],
  };
}

/**
 * The room around the desk for `theme`, seeded. Pieces that cannot be placed
 * clear of everything are left out rather than squeezed in.
 */
export function planProps(
  frame: WorkspaceFrame,
  theme: ResolvedTheme,
  seed: number,
  opts: PlanPropsOptions = {}
): RoomPlan {
  const rand = seededRandom(seed);
  const panel = opts.panel ? { bearingDeg: opts.panel.bearingDeg, halfWidthDeg: opts.panel.widthDeg / 2 } : null;
  const obstacles = obstaclesOf(frame, panel);
  const a = frame.anchor;
  // Props prefer the side the panel is not on.
  const panelSide = panel ? Math.sign(panel.bearingDeg) : 0;
  const selection = topicSelection(theme, opts.chapterSeed ?? seed, opts.topicIndex ?? 0);

  // What to place, in priority order: this topic's signature piece, a side
  // table, its 1–2 other floor pieces, maybe a second table.
  type Want = { kind: FloorKind | 'table'; radius: number };
  const floorWant = (kind: FloorKind): Want => ({ kind, radius: FLOOR_SIZE[kind].radius });
  const table: Want = { kind: 'table', radius: SIDE_TABLE.radius };
  const wants: Want[] = [floorWant(selection.signature), table];
  const extraFloor = 1 + Math.floor(rand() * 2);
  for (const kind of selection.extras.slice(0, extraFloor)) wants.push(floorWant(kind));
  if (theme.band === 'young' && rand() < 0.7) wants.push(floorWant('bigBlocks'));
  if (rand() < 0.55) wants.push(table);
  // The topic's table items, handed out in order: 2–3 per table, never repeated.
  const tableItems = [...selection.tableItems];

  const placed: PlacedProp[] = [];
  const MAX_PROPS = 7;
  for (const want of wants) {
    if (placed.length >= MAX_PROPS) break;
    const scale = 0.9 + rand() * 0.2;
    const r = want.radius * scale;
    let spot: { x: number; z: number } | null = null;
    for (let attempt = 0; attempt < 80 && !spot; attempt += 1) {
      const side = panelSide !== 0 && rand() < 0.7 ? -panelSide : rand() < 0.5 ? -1 : 1;
      const b = side * (obstacles.deskHalfAngleDeg + BEHIND_DESK_MARGIN_DEG + rand() * (PROP_MAX_BEARING - obstacles.deskHalfAngleDeg - BEHIND_DESK_MARGIN_DEG));
      const d = PROP_RING.min + rand() * (PROP_RING.max - PROP_RING.min);
      const dir = bearing(a.forward, b);
      const x = a.position.x + dir.x * d;
      const z = a.position.z + dir.z * d;
      if (!propIsClear(frame, obstacles, x, z, r)) continue;
      if (placed.some((p) => Math.hypot(p.x - x, p.z - z) < p.radius + r + PROP_GAP)) continue;
      spot = { x, z };
    }
    if (!spot) continue;
    const facing = yawTowards(new THREE.Vector3(spot.x, 0, spot.z), a.position);
    const yaw = facing + (rand() - 0.5) * ((50 * Math.PI) / 180);
    if (want.kind === 'table') {
      // The first table takes 2–3 of the topic's items, a second takes the rest.
      const count = placed.some((p) => p.type === 'table') ? tableItems.length : Math.min(tableItems.length, 2 + Math.floor(rand() * 2));
      if (count === 0) continue;
      const items = tableItems.splice(0, count).map((kind) => ({ kind, variant: Math.floor(rand() * 1000) }));
      placed.push({ type: 'table', x: spot.x, z: spot.z, yaw, radius: r, height: SIDE_TABLE.height, items });
    } else {
      placed.push({
        type: 'floor',
        kind: want.kind,
        x: spot.x,
        z: spot.z,
        yaw,
        scale,
        radius: r,
        height: FLOOR_SIZE[want.kind].height * scale,
        variant: Math.floor(rand() * 1000),
        ...(want.kind === 'easel' ? { easel: selection.board } : {}),
      });
    }
  }
  return { theme, props: placed };
}

export interface LessonRoomContext {
  chapterId?: string | null;
  topicId?: string | null;
  /** 0-based position of the topic in its chapter. */
  topicIndex?: number | null;
}

/**
 * A lesson's room, the one way the player and the Studio preview both build
 * it: the chapter shuffles the theme's catalogue once, the topic takes its own
 * slice (so a chapter's topics differ), the topic seeds where everything
 * stands, and "Shuffle" (propSeed) moves the topic on to the next slice and a
 * new arrangement.
 */
export function planLessonRoom(
  frame: WorkspaceFrame,
  theme: ResolvedTheme,
  room: LessonRoomContext,
  propSeed: number,
  panel?: { bearingDeg: number; widthDeg: number } | null
): RoomPlan {
  const index = Math.max(0, Math.floor(room.topicIndex ?? 0));
  return planProps(frame, theme, roomSeed(room.chapterId, room.topicId, propSeed), {
    panel,
    topicIndex: index + Math.max(0, Math.floor(propSeed)),
    chapterSeed: roomSeed(room.chapterId, '', 0),
  });
}
